/**
 * DOG PROFILE SCHEMA
 * Schema dla kreatora profilu psa - PROFILE entity type
 * 
 * ============================================
 * WAGI SCORE (suma = 100) - dostosowane dla PROFILE:
 * ============================================
 * 
 * === MUST completeness: 40 pkt ===
 * - dog_name (imię rodowe): 10 pkt
 * - sex (płeć): 5 pkt
 * - breed (rasa): 10 pkt
 * - main_photo: 8 pkt
 * - gallery min 3: 7 pkt
 * 
 * === Images: 15 pkt ===
 * - main_photo quality: 8 pkt
 * - gallery count >= 3: 7 pkt
 * 
 * === Trust: 30 pkt (zwiększone - brak CTA/Offer) ===
 * - health_tests (min 1): 10 pkt
 * - titles/achievements: 10 pkt
 * - registry_no / pedigree info: 5 pkt
 * - parentage info: 5 pkt
 * 
 * === Profile Completeness: 15 pkt ===
 * - birth_date: 5 pkt
 * - description: 5 pkt
 * - color: 5 pkt
 * 
 * === CTA/Flow: 0 pkt (nie dotyczy basic profile) ===
 * === Offer/Price: 0 pkt (nie dotyczy) ===
 * 
 * ============================================
 */

import { FIELD_PRIORITY, CORE_SECTION } from '../core/CoreTypes';

// ==================== SCORE WEIGHTS ====================

const SCORE_WEIGHTS = {
  // MUST completeness = 40 total
  MUST_NAME: 10,
  MUST_SEX: 5,
  MUST_BREED: 10,
  MUST_MAIN_PHOTO: 8,
  MUST_GALLERY: 7,
  
  // Images = 15 total (included in MUST above)
  // Using different calculation for profile
  
  // Trust = 30 total (increased because no CTA/Offer)
  TRUST_HEALTH_TESTS: 10,
  TRUST_TITLES: 10,
  TRUST_REGISTRY: 5,
  TRUST_PARENTAGE: 5,
  
  // Profile completeness = 15 total
  PROFILE_BIRTH_DATE: 5,
  PROFILE_DESCRIPTION: 5,
  PROFILE_COLOR: 5,
};

// ==================== OPTIONS ====================

/**
 * Płeć
 */
export const SEX_OPTIONS = [
  { value: 'female', label: 'Suka (♀)', icon: '♀' },
  { value: 'male', label: 'Pies (♂)', icon: '♂' },
];

/**
 * Rola psa w hodowli
 */
export const ROLE_OPTIONS = [
  { value: 'breeding', label: 'Hodowlany' },
  { value: 'show', label: 'Wystawowy' },
  { value: 'pet', label: 'Towarzysz' },
  { value: 'working', label: 'Użytkowy' },
  { value: 'retired', label: 'Na emeryturze' },
];

/**
 * Badania zdrowotne - dynamiczne per rasa
 */
export const HEALTH_TEST_OPTIONS = {
  large_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'spondylosis', label: 'Spondyloza' },
    { id: 'dm', label: 'DM (mielopatia)' },
    { id: 'mdr1', label: 'MDR1' },
  ],
  medium_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'patella', label: 'Luxacja rzepki' },
  ],
  small_dogs: [
    { id: 'patella', label: 'Luxacja rzepki' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'heart', label: 'Badanie serca' },
    { id: 'lcp', label: 'Legg-Calvé-Perthes' },
  ],
  cats: [
    { id: 'felv_fiv', label: 'FeLV/FIV' },
    { id: 'pkd', label: 'PKD' },
    { id: 'hcm', label: 'HCM' },
  ],
  default: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'eyes', label: 'Badanie oczu' },
    { id: 'genetic', label: 'Badanie genetyczne' },
  ]
};

/**
 * Tytuły i osiągnięcia
 */
export const TITLE_OPTIONS = [
  { id: 'ch_puppy', label: 'Champion Szczeniąt' },
  { id: 'ch_youth', label: 'Młodzieżowy Champion Polski' },
  { id: 'ch_pl', label: 'Champion Polski (Ch.PL)' },
  { id: 'ch_grand', label: 'Grand Champion Polski' },
  { id: 'ch_veteran', label: 'Champion Weteranów' },
  { id: 'ch_club', label: 'Champion Klubowy' },
  { id: 'ch_int', label: 'Interchampion (C.I.B.)' },
  { id: 'winner', label: 'Zwycięzca Polski' },
  { id: 'winner_europe', label: 'Zwycięzca Europy' },
  { id: 'winner_world', label: 'Zwycięzca Świata' },
];

export const SHOW_RESULT_OPTIONS = [
  { id: 'bis', label: 'Best in Show (BIS)' },
  { id: 'bog', label: 'Best of Group (BOG)' },
  { id: 'bob', label: 'Best of Breed (BOB)' },
  { id: 'cacib', label: 'CACIB' },
  { id: 'cac', label: 'CAC' },
  { id: 'res_cacib', label: 'Res. CACIB' },
  { id: 'res_cac', label: 'Res. CAC' },
];

// ==================== FIELD DEFINITIONS ====================

export const dogProfileFields = {
  // ========== STEP 1: PODSTAWY (MUST) ==========
  kennel_id: {
    name: 'kennel_id',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Hodowla',
    type: 'select',
    step: 1,
    validation: {}
  },
  
  name: {
    name: 'name',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Imię rodowe',
    type: 'text',
    step: 1,
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100
    },
    errors: {
      required: 'Podaj imię psa',
      minLength: 'Imię musi mieć min. 2 znaki'
    }
  },
  
  gender: {
    name: 'gender',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Płeć',
    type: 'select',
    step: 1,
    options: SEX_OPTIONS,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz płeć'
    }
  },
  
  species: {
    name: 'species',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Gatunek',
    type: 'select',
    step: 1,
    default: 'dog',
    options: [
      { value: 'dog', label: 'Pies' },
      { value: 'cat', label: 'Kot' }
    ],
    validation: { required: true }
  },
  
  breed: {
    name: 'breed',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Rasa',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz rasę'
    }
  },
  
  birth_date: {
    name: 'birth_date',
    priority: FIELD_PRIORITY.SHOULD,  // SHOULD, nie MUST
    coreSection: 'identity',
    label: 'Data urodzenia',
    type: 'date',
    step: 1,
    validation: {}
  },
  
  color: {
    name: 'color',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Umaszczenie',
    type: 'text',
    step: 1,
    validation: {}
  },
  
  role: {
    name: 'role',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Rola',
    type: 'select',
    step: 1,
    options: ROLE_OPTIONS,
    default: 'breeding',
    validation: {}
  },

  // ========== STEP 2: POCHODZENIE (SHOULD) ==========
  registry_number: {
    name: 'registry_number',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Numer rodowodu / chip',
    type: 'text',
    step: 2,
    placeholder: 'np. PKR.I-12345 lub chip',
    validation: {}
  },
  
  pedigree_name: {
    name: 'pedigree_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Pełna nazwa rodowodowa',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  sire_name: {
    name: 'sire_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Ojciec (imię)',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  sire_titles: {
    name: 'sire_titles',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Tytuły ojca',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  dam_name: {
    name: 'dam_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Matka (imię)',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  dam_titles: {
    name: 'dam_titles',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Tytuły matki',
    type: 'text',
    step: 2,
    validation: {}
  },

  // ========== STEP 3: ZDROWIE (TRUST) ==========
  selected_health_tests: {
    name: 'selected_health_tests',
    priority: FIELD_PRIORITY.SHOULD,  // Part of trust requirement
    coreSection: 'trust',
    label: 'Badania zdrowotne',
    type: 'multiselect',
    step: 3,
    validation: {}
  },
  
  health_test_results: {
    name: 'health_test_results',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Wyniki badań',
    type: 'object',
    step: 3,
    validation: {}
  },
  
  health_notes: {
    name: 'health_notes',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Dodatkowe informacje zdrowotne',
    type: 'textarea',
    step: 3,
    validation: {}
  },

  // ========== STEP 4: OSIĄGNIĘCIA (OPTIONAL) ==========
  selected_titles: {
    name: 'selected_titles',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Tytuły',
    type: 'multiselect',
    step: 4,
    validation: {}
  },
  
  selected_show_results: {
    name: 'selected_show_results',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Wyniki wystawowe',
    type: 'multiselect',
    step: 4,
    validation: {}
  },
  
  achievements_text: {
    name: 'achievements_text',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Inne osiągnięcia',
    type: 'textarea',
    step: 4,
    validation: {}
  },

  // ========== STEP 5: ZDJĘCIA (MUST) ==========
  main_photo: {
    name: 'main_photo',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'images',
    label: 'Zdjęcie główne',
    type: 'image',
    step: 5,
    validation: {
      required: true
    },
    errors: {
      required: 'Dodaj zdjęcie główne'
    }
  },
  
  gallery: {
    name: 'gallery',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'images',
    label: 'Galeria zdjęć',
    type: 'gallery',
    step: 5,
    validation: {
      required: true,
      minItems: 1
    },
    errors: {
      required: 'Dodaj min. 1 zdjęcie do galerii',
      minItems: 'Dodaj min. 1 zdjęcie do galerii'
    }
  },
  
  description: {
    name: 'description',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Opis',
    type: 'textarea',
    step: 5,
    validation: {}
  },
};

// ==================== STEP CONFIGURATION ====================
// 
// 5 kroków dla DogWizard (basic profile):
// 1. Podstawy (identity) - MUST
// 2. Pochodzenie (trust) - SHOULD
// 3. Zdrowie (trust) - SHOULD (but part of trust requirement)
// 4. Osiągnięcia (trust) - OPTIONAL
// 5. Zdjęcia (images) - MUST
//

export const dogSteps = [
  {
    id: 1,
    name: 'Podstawy',
    subtitle: 'Imię, płeć i rasa',
    icon: 'dog',
    fields: ['kennel_id', 'name', 'gender', 'species', 'breed', 'birth_date', 'color', 'role'],
    mustFields: ['name', 'gender', 'breed'],
    mustGate: true
  },
  {
    id: 2,
    name: 'Pochodzenie',
    subtitle: 'Rodowód i rodzice',
    icon: 'git-branch',
    fields: ['registry_number', 'pedigree_name', 'sire_name', 'sire_titles', 'dam_name', 'dam_titles'],
    mustFields: [],
    mustGate: false  // Optional step
  },
  {
    id: 3,
    name: 'Zdrowie',
    subtitle: 'Badania zdrowotne',
    icon: 'heart',
    fields: ['selected_health_tests', 'health_test_results', 'health_notes'],
    mustFields: [],
    mustGate: false  // Optional step (but contributes to Trust score)
  },
  {
    id: 4,
    name: 'Osiągnięcia',
    subtitle: 'Tytuły i wystawy',
    icon: 'crown',
    fields: ['selected_titles', 'selected_show_results', 'achievements_text'],
    mustFields: [],
    mustGate: false  // Optional step
  },
  {
    id: 5,
    name: 'Opis',
    subtitle: 'Opis własnymi słowami',
    icon: 'sparkles',
    fields: ['description'],
    mustFields: [],
    mustGate: false  // Optional step
  },
  {
    id: 6,
    name: 'Zdjęcia',
    subtitle: 'Galeria zdjęć',
    icon: 'camera',
    fields: ['main_photo', 'gallery'],
    mustFields: ['main_photo', 'gallery'],
    mustGate: true
  }
];

// ==================== SCHEMA EXPORT ====================

export const dogProfileSchema = {
  entityType: 'PROFILE',
  profileId: 'dog_default',
  steps: dogSteps,
  fields: dogProfileFields
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Check if gallery has minimum required images
 * Zmieniono z 3 na 1 - prostszy UX dla użytkowników
 */
export function hasSufficientGallery(formData) {
  const gallery = formData.gallery || [];
  return gallery.length >= 1;
}

/**
 * Check if has any health tests
 */
export function hasHealthTests(formData) {
  const tests = formData.selected_health_tests || [];
  return tests.length >= 1;
}

/**
 * Check if has any titles
 */
export function hasTitles(formData) {
  const titles = formData.selected_titles || [];
  return titles.length >= 1;
}

/**
 * Check if has registry number
 */
export function hasRegistryNumber(formData) {
  return formData.registry_number && formData.registry_number.trim().length > 0;
}

/**
 * Check if has parentage info
 */
export function hasParentageInfo(formData) {
  return (
    (formData.sire_name && formData.sire_name.trim().length > 0) ||
    (formData.dam_name && formData.dam_name.trim().length > 0)
  );
}

/**
 * Check if has at least one trust element
 * Trust requirement: min 1 of (health_tests OR titles OR registry_no)
 */
export function hasTrustElement(formData) {
  return hasHealthTests(formData) || hasTitles(formData) || hasRegistryNumber(formData);
}

// ==================== QUALITY SCORE CALCULATION ====================

/**
 * Calculate Quality Score for Dog Profile
 * Returns: { score: 0-100, breakdown: {...}, suggestions: [...] }
 * 
 * Wagi: 40 (MUST) + 15 (Images) + 30 (Trust) + 15 (Profile) = 100
 */
export function calculateDogQualityScore(formData) {
  let score = 0;
  const breakdown = {
    must: 0,
    images: 0,
    trust: 0,
    profile: 0
  };
  const suggestions = [];

  // ========== MUST COMPLETENESS (40 pkt) ==========
  
  // Name (10 pkt)
  if (formData.name && formData.name.trim().length >= 2) {
    breakdown.must += SCORE_WEIGHTS.MUST_NAME;
  } else {
    suggestions.push({ field: 'name', message: 'Dodaj imię psa', step: 1 });
  }
  
  // Sex (5 pkt)
  if (formData.gender) {
    breakdown.must += SCORE_WEIGHTS.MUST_SEX;
  } else {
    suggestions.push({ field: 'gender', message: 'Wybierz płeć', step: 1 });
  }
  
  // Breed (10 pkt)
  if (formData.breed && formData.breed.trim().length > 0) {
    breakdown.must += SCORE_WEIGHTS.MUST_BREED;
  } else {
    suggestions.push({ field: 'breed', message: 'Wybierz rasę', step: 1 });
  }
  
  // Main photo (8 pkt)
  if (formData.main_photo) {
    breakdown.must += SCORE_WEIGHTS.MUST_MAIN_PHOTO;
  } else {
    suggestions.push({ field: 'main_photo', message: 'Dodaj zdjęcie główne', step: 5 });
  }
  
  // Gallery (7 pkt)
  if (hasSufficientGallery(formData)) {
    breakdown.must += SCORE_WEIGHTS.MUST_GALLERY;
  } else {
    const count = (formData.gallery || []).length;
    suggestions.push({ field: 'gallery', message: `Dodaj zdjęcia do galerii (masz ${count}/3 min.)`, step: 5 });
  }

  // ========== IMAGES (already included in MUST, but track separately) ==========
  if (formData.main_photo) {
    breakdown.images += 8;
  }
  if (hasSufficientGallery(formData)) {
    breakdown.images += 7;
  }

  // ========== TRUST (30 pkt) ==========
  
  // Health tests (10 pkt)
  if (hasHealthTests(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_HEALTH_TESTS;
  } else {
    suggestions.push({ field: 'selected_health_tests', message: 'Dodaj badania zdrowotne', step: 3 });
  }
  
  // Titles (10 pkt)
  if (hasTitles(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_TITLES;
  }
  
  // Registry number (5 pkt)
  if (hasRegistryNumber(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_REGISTRY;
  }
  
  // Parentage info (5 pkt)
  if (hasParentageInfo(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_PARENTAGE;
  }

  // ========== PROFILE COMPLETENESS (15 pkt) ==========
  
  // Birth date (5 pkt)
  if (formData.birth_date) {
    breakdown.profile += SCORE_WEIGHTS.PROFILE_BIRTH_DATE;
  }
  
  // Description (5 pkt)
  if (formData.description && formData.description.trim().length >= 20) {
    breakdown.profile += SCORE_WEIGHTS.PROFILE_DESCRIPTION;
  }
  
  // Color (5 pkt)
  if (formData.color && formData.color.trim().length > 0) {
    breakdown.profile += SCORE_WEIGHTS.PROFILE_COLOR;
  }

  // Calculate total (MUST + Trust + Profile)
  // Note: Images are already counted in MUST
  score = breakdown.must + breakdown.trust + breakdown.profile;
  
  // Cap at 100
  score = Math.min(100, Math.max(0, score));

  return {
    score,
    breakdown,
    suggestions,
    status: getScoreStatus(score)
  };
}

/**
 * Get status label based on score
 */
function getScoreStatus(score) {
  if (score >= 90) return { label: 'Doskonały', color: 'emerald', icon: 'star' };
  if (score >= 70) return { label: 'Bardzo dobry', color: 'green', icon: 'check' };
  if (score >= 50) return { label: 'Dobry', color: 'yellow', icon: 'trending-up' };
  if (score >= 30) return { label: 'Wymaga uzupełnienia', color: 'orange', icon: 'alert' };
  return { label: 'Rozpoczęty', color: 'red', icon: 'edit' };
}

/**
 * Get missing MUST items with user-friendly messages
 */
export function getDogMissingItems(formData) {
  const missing = [];
  
  if (!formData.name || formData.name.trim().length < 2) {
    missing.push({ 
      field: 'name', 
      label: 'Imię psa',
      step: 1,
      message: 'Podaj imię psa'
    });
  }
  
  if (!formData.gender) {
    missing.push({ 
      field: 'gender', 
      label: 'Płeć',
      step: 1,
      message: 'Wybierz płeć'
    });
  }
  
  if (!formData.breed) {
    missing.push({ 
      field: 'breed', 
      label: 'Rasa',
      step: 1,
      message: 'Wybierz rasę'
    });
  }
  
  if (!formData.main_photo) {
    missing.push({ 
      field: 'main_photo', 
      label: 'Zdjęcie główne',
      step: 5,
      message: 'Dodaj zdjęcie główne'
    });
  }
  
  if (!hasSufficientGallery(formData)) {
    const count = (formData.gallery || []).length;
    missing.push({ 
      field: 'gallery', 
      label: 'Galeria zdjęć',
      step: 5,
      message: `Dodaj zdjęcia do galerii (masz ${count}/3 min.)`
    });
  }
  
  return missing;
}

// ==================== IMAGE REQUIREMENTS ====================

export const IMAGE_REQUIREMENTS = {
  main_photo: {
    minWidth: 1200,
    minHeight: 800,
    description: 'Zdjęcie główne min. 1200×800px'
  },
  gallery: {
    minWidth: 1200,
    minHeight: 800,
    minCount: 1,
    maxCount: 15,
    description: 'Min. 1 zdjęcie, zalecana szerokość 1200px+'
  }
};

export default dogProfileSchema;
